import type { PaintedRenderer, Point } from '../../../packages/view-phaser/src/painted-renderer.ts';

/** Camera inputs never dispatch a game command. Tap/click still belongs to the world. */
export class CameraControls {
  #renderer: PaintedRenderer;
  #blocked: () => boolean;
  #abort = new AbortController();
  #keys = new Set<string>();
  #drag: { id: number; x: number; y: number; moved: boolean } | null = null;
  #swallow = false;
  #signature = '';
  #root = document.getElementById('play')!;
  #viewport = document.getElementById('viewport')!;
  #dock = document.getElementById('camera-controls')!;

  constructor(renderer: PaintedRenderer, blocked: () => boolean) {
    this.#renderer = renderer; this.#blocked = blocked;
    const surface = this.#viewport.parentElement!, signal = this.#abort.signal;
    const world = (target: EventTarget | null): boolean => target instanceof Element && !!target.closest('#viewport,#world-controls,#combat-tags');
    surface.addEventListener('wheel', event => {
      if (blocked() || !world(event.target) || event.ctrlKey) return;
      event.preventDefault();
      const box = this.#viewport.getBoundingClientRect();
      const anchor: Point = [(event.clientX - box.left) * 1280 / box.width, (event.clientY - box.top) * 800 / box.height];
      renderer.zoom(Math.exp(-Math.max(-200, Math.min(200, event.deltaY * (event.deltaMode === 1 ? 16 : 1))) * .002), anchor);
    }, { signal, passive: false });
    surface.addEventListener('pointerdown', event => {
      this.#swallow = false;
      if (blocked() || !world(event.target) || !(event.pointerType === 'touch' || event.button === 1 || event.button === 2)) return;
      this.#swallow = false;
      this.#drag = { id: event.pointerId, x: event.clientX, y: event.clientY, moved: false };
      if (event.button === 1 || event.button === 2) event.preventDefault();
    }, { signal, capture: true });
    surface.addEventListener('pointermove', event => {
      const drag = this.#drag;
      if (!drag || drag.id !== event.pointerId || blocked()) return;
      const dx = event.clientX - drag.x, dy = event.clientY - drag.y;
      if (!drag.moved && Math.hypot(dx, dy) < 5) return;
      drag.moved = true; this.#swallow = true;
      surface.setPointerCapture(event.pointerId); event.preventDefault(); event.stopPropagation();
      const box = this.#viewport.getBoundingClientRect();
      renderer.pan(-dx * 1280 / box.width, -dy * 800 / box.height);
      drag.x = event.clientX; drag.y = event.clientY;
      this.#root.dataset.cameraDrag = 'true';
    }, { signal, capture: true });
    const release = (event: PointerEvent): void => {
      if (this.#drag?.id !== event.pointerId) return;
      if (surface.hasPointerCapture(event.pointerId)) surface.releasePointerCapture(event.pointerId);
      this.#drag = null; delete this.#root.dataset.cameraDrag;
    };
    surface.addEventListener('pointerup', release, { signal });
    surface.addEventListener('pointercancel', release, { signal });
    surface.addEventListener('click', event => {
      if (!this.#swallow) return;
      this.#swallow = false; event.preventDefault(); event.stopImmediatePropagation();
    }, { signal, capture: true });
    surface.addEventListener('contextmenu', event => { if (world(event.target)) event.preventDefault(); }, { signal });
    this.#dock.addEventListener('click', event => {
      const action = (event.target as Element).closest<HTMLButtonElement>('[data-camera]')?.dataset.camera;
      if (!action || blocked()) return;
      if (action === 'survey') { if (renderer.camera.overview) renderer.follow(); else renderer.survey(); }
      else if (action === 'follow') renderer.follow();
      else renderer.zoom(action === 'in' ? 1.25 : .8);
      this.#viewport.focus({ preventScroll: true });
    }, { signal });
    document.addEventListener('keydown', event => {
      if (blocked() || event.ctrlKey || event.metaKey || event.altKey || (event.target instanceof Element && event.target.closest('input,select,textarea,[role=dialog],dialog'))) return;
      if (!['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'f', 'F', 'v', 'V', '+', '=', '-'].includes(event.key)) return;
      event.preventDefault(); event.stopImmediatePropagation();
      if (event.key.startsWith('Arrow')) this.#keys.add(event.key);
      else if (!event.repeat) {
        if (event.key.toLowerCase() === 'f') renderer.follow();
        else if (event.key.toLowerCase() === 'v') { if (renderer.camera.overview) renderer.follow(); else renderer.survey(); }
        else renderer.zoom(event.key === '-' ? .8 : 1.25);
      }
    }, { signal, capture: true });
    document.addEventListener('keyup', event => this.#keys.delete(event.key), { signal });
    window.addEventListener('blur', () => { this.#keys.clear(); this.#drag = null; this.#swallow = false; delete this.#root.dataset.cameraDrag; }, { signal });
  }

  frame(delta: number, bounds: DOMRect, wrap: DOMRect): void {
    const width = Math.min(bounds.width, wrap.width) * 1280 / bounds.width;
    const height = Math.min(bounds.height, wrap.height) * 800 / bounds.height;
    this.#renderer.viewport(width, height);
    const blocked = this.#blocked();
    if (blocked) this.#keys.clear();
    else {
      const x = Number(this.#keys.has('ArrowRight')) - Number(this.#keys.has('ArrowLeft'));
      const y = Number(this.#keys.has('ArrowDown')) - Number(this.#keys.has('ArrowUp'));
      if (x || y) this.#renderer.pan(x * delta * .65, y * delta * .65);
    }
    const camera = this.#renderer.camera, signature = `${blocked},${camera.detached},${camera.overview}`;
    if (signature === this.#signature) return;
    this.#signature = signature;
    this.#dock.hidden = blocked;
    this.#root.toggleAttribute('data-survey', camera.overview);
    this.#root.toggleAttribute('data-camera-free', camera.detached);
    this.#dock.querySelector('[data-camera=survey]')!.setAttribute('aria-pressed', String(camera.overview));
    document.getElementById('camera-status')!.textContent = camera.overview ? 'The whole district · tap a landmark' : camera.detached ? 'Looking around · Find me returns to you' : 'Your view · Survey shows the whole district';
  }
  dispose(): void { this.#abort.abort(); this.#keys.clear(); this.#drag = null; delete this.#root.dataset.survey; delete this.#root.dataset.cameraFree; delete this.#root.dataset.cameraDrag; }
}
